import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  isValidSignupUsername,
  normalizeSignupUsername,
} from '@/lib/signup-username'
import { useTRPC } from '@/lib/trpc'
import {
  useInvalidateSocial,
  useSocialWorkspace,
} from '@/social/social-provider'
import { useSocialText } from '@/social/use-social-text'
import { useMutation } from '@tanstack/react-query'
import { AtSign, Loader2, TriangleAlert } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

export function UsernameEditor({
  currentUsername,
}: {
  currentUsername: string
}) {
  const trpc = useTRPC()
  const text = useSocialText()
  const invalidateSocial = useInvalidateSocial()
  const { publishDataEvent } = useSocialWorkspace()
  const [username, setUsername] = useState(currentUsername)
  const mutation = useMutation(trpc.socialRouter.setUsername.mutationOptions())
  const normalized = normalizeSignupUsername(username)
  const canSubmit
    = isValidSignupUsername(normalized)
      && normalized !== currentUsername
      && !mutation.isPending

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit)
      return

    try {
      await mutation.mutateAsync({ username: normalized })
      publishDataEvent('relationship.changed')
      await invalidateSocial()
      toast.success(text('social.username.changeSuccess', 'Username changed.'))
    }
    catch {
      await invalidateSocial()
      toast.error(
        text(
          'social.username.changeError',
          'Could not change that username. It may already be unavailable.',
        ),
      )
    }
  }

  return (
    <div
      className="space-y-4 rounded-lg border bg-muted/20 p-4 sm:p-5"
      data-testid="username-change-panel"
    >
      <div>
        <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <AtSign className="size-5" aria-hidden="true" />
        </div>
        <h2 className="font-semibold">
          {text('social.username.changeTitle', 'Change your username')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {text(
            'social.username.changeDescription',
            'Choose carefully. After this change, your username becomes permanent.',
          )}
        </p>
      </div>

      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="username-change-input">
            {text('social.username.newLabel', 'New username')}
          </Label>
          <div className="relative">
            <AtSign className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="username-change-input"
              value={username}
              onChange={event =>
                setUsername(normalizeSignupUsername(event.target.value))}
              placeholder={text('social.username.placeholder', 'your_username')}
              autoComplete="username"
              aria-describedby="username-change-rules username-change-warning"
              minLength={3}
              maxLength={24}
              className="pl-9"
              data-testid="username-change-input"
            />
          </div>
          <p
            id="username-change-rules"
            className="text-xs text-muted-foreground"
          >
            {text(
              'social.username.rules',
              '3–24 characters. Start with a letter; use lowercase letters, numbers, or underscores.',
            )}
          </p>
          <div
            id="username-change-warning"
            className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-foreground"
            data-testid="username-change-warning"
          >
            <TriangleAlert
              className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <p>
              {text(
                'social.username.changeWarning',
                'You can change your username once. This permanently changes your public profile URL; your old URL will redirect here.',
              )}
            </p>
          </div>
        </div>
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={!canSubmit}
          data-testid="username-change-submit"
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          {mutation.isPending
            ? text('social.username.saving', 'Saving…')
            : text('social.username.change', 'Change username')}
        </Button>
      </form>
    </div>
  )
}
